import ExporterForm from '@/components_v2/Formularies/ExporterForm';
import { FetchDynamicOptionsArgs } from '@/components_v2/Questions/context/DynamicOptionsContext';
import { useModals } from '@/hooks/ModalManager';
import { BaseExportData } from '@/hooks/RemoteFilesListener/interfaces';
import { FormularyAnswers } from '@/modules/core/domain/interfaces/IFormulary';
import { HttpResponse } from '@/modules/shared/data/protocols/http';
import { IExporterService } from '@/services/exporter';
import { ErrorHandler } from '@/services/formHandle';
import {
  Heading,
  PlacementWithLogical,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverHeader,
  PopoverTrigger,
  Portal,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useNotificationToast } from '../WCNotificationToast/WCNotificationToast';

const withPortal = (children: React.ReactNode, enabled = true) => {
  if (!enabled) return children;
  return <Portal>{children}</Portal>;
};

interface ExporterContextMenuProps {
  button: JSX.Element | ((isOpen: boolean) => JSX.Element) | undefined;
  screenName: string;
  getExporter: (screenName: string) => IExporterService;
  options?: Record<string, string | boolean>;
  placement?: PlacementWithLogical;
  waitForExporter?: boolean;
  hasPortal?: boolean;
  onSuccess?: (data: BaseExportData & { token: string }) => void;
  formButtonLabel?: 'PDF';
  screen?: string;
}

/**
 * Runs an export request and, when the API answers 409 `pending_media`
 * (event still has media being processed), asks the user whether to export
 * anyway. Resolves `null` when the user cancels.
 */
export function usePendingMediaConfirm() {
  const { t } = useTranslation();
  const modals = useModals();

  return useCallback(
    async (
      request: (forceExport: boolean) => Promise<HttpResponse<any>>,
    ): Promise<HttpResponse<any> | null> => {
      const response = await request(false);
      if (
        response.statusCode !== 409 ||
        response.body?.status !== 'pending_media'
      ) {
        return response;
      }

      const confirmed = await new Promise<boolean>((resolve) => {
        modals.confirm({
          title: t('error.warning'),
          children: t('export.pending_media_warning', {
            count: response.body.pending_media_count ?? 0,
          }),
          cancelLabel: t('common.cancel'),
          confirmLabel: t('export.export_anyway'),
          confirmProps: { colorScheme: 'red' },
          onConfirm: () => resolve(true),
          onCancel: () => resolve(false),
          onClose: () => resolve(false),
        });
      });

      return confirmed ? request(true) : null;
    },
    [modals, t],
  );
}

export function useSimpleExporter({
  screenName,
  getExporter,
  options,
  onSuccess,
  exportType = 'pdf',
  objectId = null,
  payload = {},
}: {
  screenName: string;
  objectId?: string | undefined | null;
  payload?: Record<string, any>;
  exportType: 'pdf' | 'xlsx' | 'csv' | 'zip';
  onSuccess?: (data: BaseExportData & { token: string }) => void;
  getExporter: (screenName: string) => IExporterService;
  options?: Record<string, string | boolean>;
}) {
  const { t } = useTranslation();
  const notify = useNotificationToast();
  const toast = useToast();
  const withPendingMediaConfirm = usePendingMediaConfirm();

  const exporter = useMemo(() => getExporter(screenName), [
    getExporter,
    screenName,
  ]);

  const onExport = useCallback(async () => {
    const response = await withPendingMediaConfirm((force) =>
      exporter.execute(
        {
          ...payload,
          parentable_type: screenName,
          export_type: exportType,
          options: {
            objectId,
            ...options,
          },
        },
        force,
      ),
    );
    if (!response) return;

    const { body, statusCode } = response;

    if (body && body.downloadFileUrl) {
      toast.closeAll();

      setTimeout(() => {
        notify({
          id: body.downloadFileUrl,
          duration: 8000,
          title: t('notification.export_success'),
          notificationType: 'file',
          onClick: () => window.open(body?.downloadFileUrl),
        });
      }, 1);
    } else if (statusCode >= 200 && statusCode < 300) {
      toast({
        status: 'info',
        title: t('common.success'),
        description: t('notification.export_starting'),
      });
    } else {
      const formattedError: string = ErrorHandler(body.error_messages);
      toast({
        status: 'error',
        title: t('error.warning'),
        description: t(formattedError),
      });
    }
    onSuccess?.(response.body);
  }, [
    exporter,
    withPendingMediaConfirm,
    payload,
    screenName,
    exportType,
    objectId,
    options,
    onSuccess,
    toast,
    notify,
    t,
  ]);

  return { onExport };
}

const ExporterContextMenu: React.FC<ExporterContextMenuProps> = ({
  button,
  screenName,
  getExporter,
  options,
  onSuccess,
  placement,
  hasPortal = true,
  formButtonLabel,
  screen,
}) => {
  const { t } = useTranslation();
  const exporter = useMemo(() => getExporter(screenName), [
    getExporter,
    screenName,
  ]);
  const withPendingMediaConfirm = usePendingMediaConfirm();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const onSubmit = useCallback(
    (onClose: () => void) => async (data: FormularyAnswers) => {
      const params = Object.entries(data).reduce((prev, curr) => {
        const [key, value] = curr;
        return {
          ...prev,
          [key]: value.values,
        };
      }, {});
      const response = await withPendingMediaConfirm((force) =>
        exporter.execute(
          {
            ...params,
            parentable_type: screenName,
            options,
            token,
          },
          force,
        ),
      );
      if (!response) {
        // user cancelled: keep the popover open, nothing was created
        return { statusCode: 0, body: undefined } as HttpResponse<any>;
      }
      onClose();
      onSuccess?.(response.body);
      return response;
    },
    [exporter, screenName, options, onSuccess, token, withPendingMediaConfirm],
  );

  const templateFetcher = useCallback(async () => {
    const template = await exporter.getTemplate(
      `&parentable_type=${screenName}${screen ? `&screen=${screen}` : ''}${
        token ? `&token=${token}` : ''
      }${options?.objectId ? `&event_id=${options?.objectId}` : ''}`,
    );
    return template;
  }, [exporter, screenName, screen, options?.objectId]);

  const nestedFilterFetcher = useCallback(
    ({ filterQuestionId, filterBy }: FetchDynamicOptionsArgs) =>
      exporter.getNestedFilterOptions({
        parentableType: screenName,
        filterQuestionId,
        filterBy,
      }),
    [exporter, screenName],
  );

  return (
    <Popover closeOnBlur={false} trigger="click" isLazy placement={placement}>
      {({ onClose, isOpen }) => (
        <>
          <PopoverTrigger>{button}</PopoverTrigger>
          {isOpen && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: 3,
              }}
              onClick={onClose}
              aria-hidden="true"
            />
          )}

          {withPortal(
            <PopoverContent maxWidth="400px" minWidth="300px" zIndex={999}>
              <PopoverCloseButton />
              <PopoverArrow />
              <PopoverHeader>
                <Heading size="md">{t('common.export')}</Heading>
              </PopoverHeader>
              <PopoverBody maxH="400px" overflowY="auto">
                <ExporterForm
                  buttonLabel={formButtonLabel}
                  submitHandler={onSubmit(onClose)}
                  fetchTemplate={templateFetcher}
                  fetchNestedFilter={nestedFilterFetcher}
                  screenName={screenName}
                />
              </PopoverBody>
            </PopoverContent>,
            hasPortal,
          )}
        </>
      )}
    </Popover>
  );
};

export default ExporterContextMenu;
